import { Box, Typography, Tabs, TabList, Tab, TabPanel, Card, Stack, Avatar, Button, Chip, CircularProgress, Alert, Input, IconButton, Tooltip, Link } from "@mui/joy";
import { PeopleRounded, LocalHospitalRounded, EditRounded, DeleteRounded, DeleteForeverRounded, LinkRounded, ChatRounded, ContentCopyRounded, RefreshRounded, CheckRounded, BlockRounded, LockOpenRounded, PersonSearchRounded } from '@mui/icons-material';
import { useContext, useEffect, useState, useRef, useCallback } from "react";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import { LayoutContext } from "../context";
import { APIGetPatients, APIGetDoctorPatients, APICreateConnectToken, APIGetConnectTokens, APIDeactivateConnectToken, APIDeleteProfile, APIPurgeProfile, APIToggleBlockProfile, APIGetDoctors, APIImportDoctorsFromMIS, TELEGRAM_BOT_LINK, TELEGRAM_PATIENT_BOT_LINK, APICreateChatWithUser, APIAssignDoctor } from "../api";
import { useNavigate } from "react-router";
import { USER_TYPES } from "../components/modals/ModalUser";
import ModalSelectDoctor from "../components/modals/ModalSelectDoctor";



export default function UsersPage() {
    const { currentDoctor, token, setModalUser } = useContext(LayoutContext);
    const [activeTab, setActiveTab] = useState(0);
    
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState({ patients: false, doctors: false });
    const [loadingMore, setLoadingMore] = useState({ patients: false, doctors: false });
    const [hasMore, setHasMore] = useState({ patients: true, doctors: true });
    const [page, setPage] = useState({ patients: 1, doctors: 1 });
    const PAGE_SIZE = 20;

    const [generatingTokens, setGeneratingTokens] = useState({});
    const [connectTokens, setConnectTokens] = useState({});
    const [blocking, setBlocking] = useState({});
    const [deleting, setDeleting] = useState({});
    const [purging, setPurging] = useState({});
    const [showInactive, setShowInactive] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [copiedToken, setCopiedToken] = useState(null);
    const [importingDoctors, setImportingDoctors] = useState(false);
    const [assignFor, setAssignFor] = useState(null);   // пациент, которому назначаем специалиста
    const [assigning, setAssigning] = useState(false);

    const assignDoctor = async (patient, doctorId) => {
        if (!patient) return;
        setAssigning(true);
        try {
            const resp = await APIAssignDoctor(patient.id, doctorId, token);
            setAssignFor(null);
            setSuccess(resp?.message || 'Специалист назначен');
            loadAllData();
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось назначить специалиста');
        } finally {
            setAssigning(false);
        }
    };

    const handleImportDoctors = async () => {
        setImportingDoctors(true);
        setError(null);
        setSuccess(null);
        try {
            const resp = await APIImportDoctorsFromMIS(token);
            const r = resp?.result || {};
            setSuccess(
                `Импорт врачей из МИС: создано ${r.created ?? 0}, обновлено ${r.updated ?? 0}`
                + (r.skipped ? `, пропущено ${r.skipped}` : '')
                + (r.errors ? `, ошибок ${r.errors}` : '')
            );
            await loadAllData();
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось импортировать врачей из МИС');
        } finally {
            setImportingDoctors(false);
        }
    };

    const isAdmin = (currentDoctor?.type_profile.includes('admin') || currentDoctor?.type_profile?.includes('admin')) || (currentDoctor?.type_profile.includes('supervisor') || currentDoctor?.type_profile?.includes('supervisor'));

    // Загрузка всех данных при монтировании
    const loadAllData = async () => {
        try {
            setLoading({ patients: true, doctors: true });
            setError(null);
            
            const [patientsResult, doctorsResult] = await Promise.all([
                loadPatientsData(1),
                isAdmin ? loadDoctorsData(1) : Promise.resolve({ items: [], totalPages: 1 })
            ]);
            
            setPatients(patientsResult.items);
            setPage(prev => ({ ...prev, patients: 1 }));
            setHasMore(prev => ({ ...prev, patients: 1 < patientsResult.totalPages }));
            
            if (isAdmin) {
                setDoctors(doctorsResult.items);
                setPage(prev => ({ ...prev, doctors: 1 }));
                setHasMore(prev => ({ ...prev, doctors: 1 < doctorsResult.totalPages }));
            }
            
            await loadConnectTokens();
            
        } catch (error) {
            console.error("Ошибка при загрузке данных:", error);
            setError("Ошибка при загрузке данных");
        } finally {
            setLoading({ patients: false, doctors: false });
        }
    };

    const loadMore = async (type) => {
        if (loadingMore[type] || !hasMore[type]) return;
        
        try {
            setLoadingMore(prev => ({ ...prev, [type]: true }));
            const nextPage = page[type] + 1;
            
            let result;
            if (type === 'patients') {
                result = await loadPatientsData(nextPage);
                setPatients(prev => [...prev, ...result.items]);
            } else {
                result = await loadDoctorsData(nextPage);
                setDoctors(prev => [...prev, ...result.items]);
            }
            
            setPage(prev => ({ ...prev, [type]: nextPage }));
            setHasMore(prev => ({ ...prev, [type]: nextPage < result.totalPages }));
            
            // Подгружаем токены для новых профилей
            await loadConnectTokens();
            
        } catch (error) {
            console.error(`Ошибка при подгрузке ${type}:`, error);
        } finally {
            setLoadingMore(prev => ({ ...prev, [type]: false }));
        }
    };

    // Рефы для наблюдателей
    const patientsObserverRef = useRef(null);
    const doctorsObserverRef = useRef(null);

    // Последний элемент списка пациентов
    const lastPatientRef = useCallback((node) => {
        if (loading.patients || loadingMore.patients) return;
        if (patientsObserverRef.current) patientsObserverRef.current.disconnect();
        
        patientsObserverRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore.patients) {
                loadMore('patients');
            }
        }, { threshold: 0.1 });
        
        if (node) patientsObserverRef.current.observe(node);
    }, [loading.patients, loadingMore.patients, hasMore.patients, page.patients]);

    // Последний элемент списка докторов
    const lastDoctorRef = useCallback((node) => {
        if (loading.doctors || loadingMore.doctors) return;
        if (doctorsObserverRef.current) doctorsObserverRef.current.disconnect();
        
        doctorsObserverRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore.doctors) {
                loadMore('doctors');
            }
        }, { threshold: 0.1 });
        
        if (node) doctorsObserverRef.current.observe(node);
    }, [loading.doctors, loadingMore.doctors, hasMore.doctors, page.doctors]);

    // Загрузка данных пациентов
    const loadPatientsData = async (pageNum = 1) => {
        try {
            let response;
            const params = { page: pageNum, page_size: PAGE_SIZE };
            if (showInactive) params.include_inactive = 'true';
            if (isAdmin) {
                response = await APIGetPatients(token, params);
                return { items: response.profiles || [], totalPages: response.total_pages || 1 };
            } else {
                response = await APIGetDoctorPatients(token, params);
                return { items: response.patients || [], totalPages: response.total_pages || 1 };
            }
        } catch (error) {
            console.error("Ошибка при загрузке пациентов:", error);
            throw error;
        }
    };

    // Загрузка данных докторов с сервера
    const loadDoctorsData = async (pageNum = 1) => {
        try {
            const params = { page: pageNum, page_size: PAGE_SIZE };
            const response = await APIGetDoctors(token, params);
            return { items: response.doctors || [], totalPages: response.total_pages || 1 };
        } catch (error) {
            console.error("Ошибка при загрузке докторов:", error);
            throw error;
        }
    };

    // Загрузка токенов подключения
    const loadConnectTokens = async () => {
        try {
            const response = await APIGetConnectTokens(token);
            const tokensMap = {};
            
            (response.tokens || []).forEach(token => {
                if (token.target_profile) {
                    tokensMap[token.target_profile.id] = token;
                }
            });
            
            setConnectTokens(tokensMap);
        } catch (error) {
            console.error("Ошибка при загрузке токенов:", error);
        }
    };

    // Генерация токена подключения
    const generateConnectToken = async (profileId) => {
        try {
            setGeneratingTokens(prev => ({ ...prev, [profileId]: true }));
            setError(null);
            
            const response = await APICreateConnectToken(token, {
                target_profile_id: profileId
            });
            
            await loadConnectTokens();
            setSuccess("Ссылка успешно сгенерирована");
            
        } catch (error) {
            console.error("Ошибка при генерации токена:", error);
            setError("Ошибка при генерации ссылки");
        } finally {
            setGeneratingTokens(prev => ({ ...prev, [profileId]: false }));
        }
    };

    // Регенерация токена
    const regenerateConnectToken = async (profileId, currentTokenId) => {
        try {
            setGeneratingTokens(prev => ({ ...prev, [profileId]: true }));
            setError(null);
            
            await APIDeactivateConnectToken(token, currentTokenId);
            await APICreateConnectToken(token, {
                target_profile_id: profileId
            });
            
            await loadConnectTokens();
            setSuccess("Ссылка успешно пересоздана");
            
        } catch (error) {
            console.error("Ошибка при регенерации токена:", error);
            setError("Ошибка при перегенерации ссылки");
        } finally {
            setGeneratingTokens(prev => ({ ...prev, [profileId]: false }));
        }
    };

    // Блокировка/разблокировка пользователя
    const toggleBlockUser = async (user, type = 'patient') => {
        try {
            setBlocking(prev => ({ ...prev, [user.id]: true }));
            setError(null);
            
            const response = await APIToggleBlockProfile(token, user.id, type);
            
            // Обновляем данные
            await loadAllData();
            
            setSuccess(
                response.is_block 
                    ? "Пользователь успешно заблокирован" 
                    : "Пользователь успешно разблокирован"
            );
            
        } catch (error) {
            console.error("Ошибка при блокировке пользователя:", error);
            setError("Ошибка при изменении статуса блокировки");
        } finally {
            setBlocking(prev => ({ ...prev, [user.id]: false }));
        }
    };

    // Удаление пользователя
    const deleteUser = async (user, type = 'patient') => {
        // Простой confirm вместо диалога
        const isConfirmed = window.confirm(
            `Вы уверены, что хотите удалить пользователя "${user.name || user.user?.full_name}"?\n\nЭто действие нельзя отменить.`
        );
        
        if (!isConfirmed) return;
        
        try {
            setDeleting(prev => ({ ...prev, [user.id]: true }));
            setError(null);
            
            await APIDeleteProfile(token, user.id, type);
            
            // Обновляем данные
            await loadAllData();
            
            setSuccess("Пользователь успешно удален");
            
        } catch (error) {
            console.error("Ошибка при удалении пользователя:", error);
            setError("Ошибка при удалении пользователя");
        } finally {
            setDeleting(prev => ({ ...prev, [user.id]: false }));
        }
    };

    // Полное удаление из базы (необратимо)
    const purgeUser = async (user) => {
        const ok = window.confirm(
            `ПОЛНОЕ удаление "${user.name || user.user?.full_name}" из базы.\n\n` +
            `Уйдут связки, участие в чатах, результаты тестов и сессии этого пользователя. ` +
            `Тексты переписки останутся, но без автора. Это действие необратимо.\n\nПродолжить?`
        );
        if (!ok) return;
        try {
            setPurging(prev => ({ ...prev, [user.id]: true }));
            setError(null);
            await APIPurgeProfile(token, user.id);
            await loadAllData();
            setSuccess("Пользователь удалён из базы");
        } catch (error) {
            console.error("Ошибка при полном удалении:", error);
            setError(error?.response?.data?.error || "Не удалось удалить пользователя");
        } finally {
            setPurging(prev => ({ ...prev, [user.id]: false }));
        }
    };

    // Копирование ссылки в буфер обмена
    const copyToClipboard = (token, type = 'patient') => {
        const link = type == 'patient' ? `${TELEGRAM_PATIENT_BOT_LINK}?start=${token}` : `${TELEGRAM_BOT_LINK}?start=${token}`;
        navigator.clipboard.writeText(link);
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2000);
        setSuccess("Ссылка скопирована в буфер обмена");
    };

    useEffect(() => {
        loadAllData();
    }, [showInactive]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const ConnectTokenSection = ({ profile, type = 'patient' }) => {
        const tokenData = connectTokens[profile.id];
        
        if (!tokenData) {
            return (
                <Button
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    loading={generatingTokens[profile.id]}
                    startDecorator={<LinkRounded />}
                    onClick={() => generateConnectToken(profile.id)}
                >
                    Сгенерировать ссылку
                </Button>
            );
        }

        if (tokenData.connected_user) {
            return (
                <Chip variant="soft" color="success" size="sm" sx={{ px: 2 }} startDecorator={<CheckRounded />}>
                    Подключен: <b>{tokenData.connected_user.full_name || tokenData.connected_user.username}</b>
                </Chip>
            );
        }

        if (tokenData.is_active) {
            const fullLink = type == 'patient' ? `${TELEGRAM_PATIENT_BOT_LINK}?start=${tokenData.token}` : `${TELEGRAM_BOT_LINK}?start=${tokenData.token}`;
            
            return (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Input 
                        value={fullLink}
                        size="sm"
                        readOnly
                        endDecorator={
                            <Tooltip title={copiedToken === tokenData.token ? "Скопировано!" : "Копировать"}>
                                <IconButton 
                                    variant="plain" 
                                    onClick={() => copyToClipboard(tokenData.token, type)}
                                >
                                    {copiedToken === tokenData.token ? <CheckRounded /> : <ContentCopyRounded />}
                                </IconButton>
                            </Tooltip>
                        }
                        sx={{ width: "-webkit-fill-available" }}
                        // sx={{ minWidth: 300 }}
                    />
                    <Tooltip title="Перегенерировать ссылку">
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            size="sm"
                            loading={generatingTokens[profile.id]}
                            onClick={() => regenerateConnectToken(profile.id, tokenData.id)}
                        >
                            <RefreshRounded />
                        </IconButton>
                    </Tooltip>
                </Stack>
            );
        }

        return (
            <Button
                variant="outlined"
                color="neutral"
                size="sm"
                loading={generatingTokens[profile.id]}
                startDecorator={<LinkRounded />}
                onClick={() => generateConnectToken(profile.id)}
            >
                Сгенерировать ссылку
            </Button>
        );
    };

    const DoctorPatientBotLink = ({ profile }) => {
        if (!profile.type_profile?.includes('doctor')) return null;
        // Берём готовую ссылку с сервера (она автоматически приходит в сериализаторе).
        // Fallback на ручную сборку оставлен, если бэк по какой-то причине вернул null.
        const fullLink = profile.connection_link
            || (profile.profile_token ? `${TELEGRAM_PATIENT_BOT_LINK}?start=v_${profile.profile_token}` : null);

        if (!fullLink) return null;
        
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Input
                    value={fullLink}
                    size="sm"
                    readOnly
                    startDecorator={
                        <Chip size="sm" variant="soft" color="primary">
                            Ссылка для пациента
                        </Chip>
                    }
                    endDecorator={
                        <Tooltip title={copiedToken === `doc_${profile.profile_token}` ? "Скопировано!" : "Копировать ссылку для пациента"}>
                            <IconButton
                                variant="plain"
                                onClick={() => {
                                    navigator.clipboard.writeText(fullLink);
                                    setCopiedToken(`doc_${profile.profile_token}`);
                                    setTimeout(() => setCopiedToken(null), 2000);
                                    setSuccess("Ссылка для пациента скопирована");
                                }}
                            >
                                {copiedToken === `doc_${profile.profile_token}` ? <CheckRounded /> : <ContentCopyRounded />}
                            </IconButton>
                        </Tooltip>
                    }
                    sx={{ width: "-webkit-fill-available" }}
                />
            </Stack>
        );
    };


    function getTypeUser(types) {
        return USER_TYPES.filter(function(obj) {
            return types.includes(obj.value);
        });
    }

    const UserCard = ({ user, type = 'patient' }) => {
        const isBlocked = user.is_block || user.user?.is_block;
        const navigate = useNavigate();
        
        // Обработчик нажатия на кнопку чата
        const handleChatClick = async () => {
            try {
                // Если чат уже существует, переходим в него
                if (user?.chat_with_current_user) {
                    navigate(`/chats?chatId=${user.chat_with_current_user.id}`);
                    return;
                }
                
                // Если чата нет, создаем его
                const response = await APICreateChatWithUser(user.id, token);
                
                if (response.chat_id) {
                    // Переходим в созданный чат
                    navigate(`/chats?chatId=${response.chat_id}`);
                }
                
            } catch (error) {
                console.error("Ошибка при создании/переходе в чат:", error);
                setError("Ошибка при создании чата");
            }
        };

        return (
            <Card variant="outlined" sx={{ p: 2, mb: 1, opacity: isBlocked ? 0.7 : 1 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                        <Avatar size="lg" color={isBlocked ? "danger" : "neutral"}>
                            {user.name?.charAt(0) || user.user?.full_name?.charAt(0) || 'U'}
                        </Avatar>
                        
                        <Box sx={{ flex: 1 }}>
                            <Typography level="h4" fontSize="md">
                                {user.name || user.user?.full_name}
                                {isBlocked && (
                                    <Chip size="sm" color="danger" variant="soft" sx={{ ml: 1 }}>
                                        Заблокирован
                                    </Chip>
                                )}
                            </Typography>
                            
                            <Stack direction={'row'} flexWrap='wrap' sx={{ my: 0.5, gap: 1 }}>
                                {getTypeUser(user?.type_profile)?.map((item) => 
                                    <Chip size="sm" color="primary">
                                        {item.label}
                                    </Chip>
                                )}
                            </Stack>
                            
                            {user.user?.username && (
                                <Link to={`https://t.me/${user.user.username}`} underline="none" color="warning" endDecorator={<OpenInNewRoundedIcon/>}>
                                    @{user.user.username}
                                </Link>
                            )}
                            
                            {user.phone && (
                                <Typography level="body3" color="neutral">
                                    📞 {user.phone}
                                </Typography>
                            )}
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        {type === 'patient' && user.pending_connection_requests > 0 && (
                            <Chip variant="soft" color="warning" size="sm">
                                Заявок: {user.pending_connection_requests}
                            </Chip>
                        )}
                        <Chip 
                            variant="soft" 
                            color={user.is_active ? 'success' : 'danger'} 
                            size="sm"
                        >
                            {user.is_active ? 'Активен' : 'Неактивен'}
                        </Chip>
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: "center", sm: "flex-end" }} sx={{ width: { xs: "100%", sm: "auto" } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: "100%" }}>
                            <ConnectTokenSection profile={user} type={type} />
                            {type === 'doctor' && <DoctorPatientBotLink profile={user} />}
                            
                            {/* Обновленная кнопка чата */}
                            <Button
                                variant="soft"
                                color={user?.chat_with_current_user ? "primary" : "neutral"}
                                size="sm"
                                startDecorator={<ChatRounded />}
                                onClick={handleChatClick}
                            >
                                {user?.chat_with_current_user ? "В чат" : "Созд. чат"}
                            </Button>

                            {type === 'patient' && (
                              <Button
                                variant="soft"
                                color="primary"
                                size="sm"
                                startDecorator={<PersonSearchRounded />}
                                onClick={() => setAssignFor(user)}
                              >
                                Назначить специалиста
                              </Button>
                            )}
                        </Stack>
                        
                        <Stack direction={"row"} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                            <IconButton
                                variant="soft"
                                color="neutral"
                                size="sm"
                                onClick={() => setModalUser({ open: true, userId: user.id, onLoadData: () => loadAllData() })}
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                                <EditRounded />
                            </IconButton>
                            
                            <IconButton
                                variant="soft"
                                color="danger"
                                size="sm"
                                loading={deleting[user.id]}
                                onClick={() => deleteUser(user, type)}
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                                <DeleteRounded />
                            </IconButton>

                            {user.is_active === false && (
                              <Tooltip title="Удалить навсегда (из базы)">
                                <IconButton
                                    variant="solid"
                                    color="danger"
                                    size="sm"
                                    loading={purging[user.id]}
                                    onClick={() => purgeUser(user)}
                                    sx={{ width: { xs: "100%", sm: "auto" } }}
                                >
                                    <DeleteForeverRounded />
                                </IconButton>
                              </Tooltip>
                            )}
                            
                            {/* Кнопка блокировки/разблокировки */}
                            <Tooltip title={isBlocked ? "Разблокировать" : "Заблокировать"}>
                                <IconButton
                                    variant="soft"
                                    color={isBlocked ? "success" : "warning"}
                                    size="sm"
                                    loading={blocking[user.id]}
                                    onClick={() => toggleBlockUser(user, type)}
                                    sx={{ width: { xs: "100%", sm: "auto" } }}
                                >
                                    {isBlocked ? <LockOpenRounded /> : <BlockRounded />}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>
        );
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <Box
                component="main"
                className="MainContent"
                sx={{
                    px: { xs: 2, md: 6 },
                    pt: 2,
                    pb: { xs: 2, sm: 2, md: 3 },
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        mb: 1,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'start', sm: 'center' },
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography level="h2" component="h1">
                        Пользователи
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        {isAdmin && (
                            <Button
                                variant="outlined" color="primary"
                                startDecorator={<RefreshRounded />}
                                loading={importingDoctors}
                                onClick={handleImportDoctors}
                            >
                                Импорт врачей из МИС
                            </Button>
                        )}
                        <Button
                            startDecorator={<AddRoundedIcon />}
                            onClick={() => setModalUser({ open: true, userId: null, onLoadData: () => loadAllData() })}
                        >
                            Создать пользователя
                        </Button>
                    </Stack>
                </Box>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert color="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderRadius: 'xl' }}>
                    <TabList>
                        <Tab sx={{ borderTopLeftRadius: 15 }}>
                            <PeopleRounded sx={{ mr: 1 }} />
                            Пациенты
                            {patients.length > 0 && (
                                <Chip size="sm" variant="soft" color="primary" sx={{ ml: 1 }}>
                                    {patients.length}
                                </Chip>
                            )}
                        </Tab>
                        
                        {isAdmin && (
                            <Tab>
                                <LocalHospitalRounded sx={{ mr: 1 }} />
                                Специалисты
                                {doctors.length > 0 && (
                                    <Chip size="sm" variant="soft" color="primary" sx={{ ml: 1 }}>
                                        {doctors.length}
                                    </Chip>
                                )}
                            </Tab>
                        )}
                    </TabList>

                    <TabPanel value={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Button
                                size="sm"
                                variant={showInactive ? 'solid' : 'soft'}
                                color="neutral"
                                startDecorator={<DeleteRounded />}
                                onClick={() => setShowInactive(v => !v)}
                            >
                                {showInactive ? 'Скрыть удалённых' : 'Показать удалённых'}
                            </Button>
                        </Box>
                        {loading.patients ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : patients.length > 0 ? (
                            <Stack spacing={1}>
                                {patients.map((patient, index) => (
                                    <Box
                                        key={patient.id}
                                        ref={index === patients.length - 1 ? lastPatientRef : null}
                                    >
                                        <UserCard 
                                            user={patient} 
                                            type="patient" 
                                        />
                                    </Box>
                                ))}
                                {loadingMore.patients && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                )}
                            </Stack>
                        ) : (
                            <Alert color="neutral" sx={{ mt: 2 }}>
                                {isAdmin 
                                    ? "Пациенты не найдены" 
                                    : "У вас нет прикрепленных пациентов"
                                }
                            </Alert>
                        )}
                    </TabPanel>

                    {isAdmin && (
                        <TabPanel value={1}>
                            {loading.doctors ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : doctors.length > 0 ? (
                                <Stack spacing={1}>
                                    {doctors.map((doctor, index) => (
                                        <Box
                                            key={doctor.id}
                                            ref={index === doctors.length - 1 ? lastDoctorRef : null}
                                        >
                                            <UserCard 
                                                user={doctor} 
                                                type="doctor" 
                                            />
                                        </Box>
                                    ))}
                                    {loadingMore.doctors && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size="sm" />
                                        </Box>
                                    )}
                                </Stack>
                            ) : (
                                <Alert color="neutral" sx={{ mt: 2 }}>
                                    Доктора не найдены
                                </Alert>
                            )}
                        </TabPanel>
                    )}
                </Tabs>
            </Box>

            <ModalSelectDoctor
              open={!!assignFor}
              onClose={() => setAssignFor(null)}
              token={token}
              submitting={assigning}
              title="Назначить специалиста"
              onSelect={(doc) => assignDoctor(assignFor, doc.id)}
            />
        </Box>
    );
}